var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { PrimaryGrid } from "../../components/grid/PrimaryGrid";
import PrimaryChip from "../../components/chip/PrimaryChip";
import React, { useEffect, useMemo, useState } from "react";
import { PageHeader } from "../../components/header/PageHeader";
import { WidgetLoader } from "../../components/loader/WidgetLoader";
import { NoModuleAccess } from "../../components/no-module-access/NoModuleAccess";
import { Button, Box, useMediaQuery, IconButton, Typography, Switch, Avatar, Tooltip, useTheme, alpha, } from "@mui/material";
import { SettingUserCard } from "@ui/components/card/SettingUserCard";
import { CardPagination } from "@ui/components/pagination/CardPagination";
import { Add, Edit, SearchNormal1 } from "iconsax-react";
import TrashIcon from "@ui/components/icons/TrashIcon";
import { AddEditUserDialog } from "./AddEditUserDialog";
import ConfirmDialog from "@ui/components/dialog/Confirmation";
import { useCreateUserMutation, useGetUsersSummaryQuery, useUpdateUserStatusMutation, useDeleteUserMutation, useUpdateUserMutation, useGetAllCompaniesQuery, } from "../../../features/projects/projectsApi";
import { useSnackbar } from "notistack";
import { useAuth } from "../../../auth/useAuth";
import { usePermissions } from "../../../context/PermissionContext";
import { FilteredInput } from "@ui/components/inputs";
export const Users = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery((theme) => theme.breakpoints.down("md"));
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    const [newUserDialogOpen, setNewUserDialogOpen] = useState(false);
    const [editUser, setEditUser] = useState(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const { account } = useAuth();
    const { canRead, canCreate, canUpdate, canDelete, isLoading: permissionsLoading, } = usePermissions();
    const { data: allCompanies } = useGetAllCompaniesQuery();
    const [createUser, { isLoading: isCreating }] = useCreateUserMutation();
    const [updateUserStatus] = useUpdateUserStatusMutation();
    const [deleteUser, { isLoading: isDeleting }] = useDeleteUserMutation();
    const [updateUser, { isLoading: isUpdating }] = useUpdateUserMutation();
    const { data: usersSummary, isLoading, refetch } = useGetUsersSummaryQuery();
    const { enqueueSnackbar } = useSnackbar();
    // Map API data to User interface
    const users = (usersSummary || [])
        .filter((user) => user.id) // Only include users with valid id
        .map((user) => {
        // Map company IDs to company names using all companies
        const userCompanyNames = user.allCompanies
            ? (allCompanies || []).map((c) => c.entiteitNaam)
            : (user.companyIds || [])
                .map((companyId) => {
                const company = allCompanies === null || allCompanies === void 0 ? void 0 : allCompanies.find((c) => c.entiteitId.toLowerCase() === companyId.toLowerCase());
                return company === null || company === void 0 ? void 0 : company.entiteitNaam;
            })
                .filter((name) => name !== undefined);
        return {
            id: user.id,
            avatar: "/assets/avatars/avatar-default.jpg",
            name: user.username || user.userEmail.split("@")[0],
            email: user.userEmail,
            companies: userCompanyNames,
            allCompanies: user.allCompanies,
            status: user.isActive,
            roleId: user.roleId,
            roleName: user.roleName,
        };
    });
    const getUserInitials = (name) => {
        const words = name.split(" ");
        if (words.length >= 2) {
            return (words[0][0] + words[1][0]).toUpperCase();
        }
        return name.substring(0, 2).toUpperCase();
    };
    const createUserColumns = (onEdit, onDelete, onStatusChange) => [
        {
            field: "name",
            headerName: "Naam",
            flex: 2,
            minWidth: 250,
            renderCell: (params) => (React.createElement(Box, { sx: { display: "flex", alignItems: "center", gap: 1 } },
                React.createElement(Avatar, { alt: params.row.name, sx: {
                        width: 32,
                        height: 32,
                        fontSize: "14px",
                        bgcolor: theme.palette.grey[300],
                        color: theme.palette.grey[600],
                    } }, getUserInitials(params.row.name)),
                React.createElement(Box, null,
                    React.createElement(Typography, { variant: "body2" }, params.row.name)))),
        },
        {
            field: "email",
            headerName: "E-mail",
            flex: 1.5,
            minWidth: 150,
        },
        {
            field: "companies",
            headerName: "Bedrijven",
            flex: 1.5,
            minWidth: 150,
            renderCell: (params) => {
                const companyCount = params.row.companies.length;
                return React.createElement(PrimaryChip, { label: companyCount });
            },
        },
        {
            field: "roleName",
            headerName: "Rol",
            flex: 1,
            minWidth: 120,
            renderCell: (params) => (React.createElement(Typography, { variant: "body2", sx: {
                    color: "text.secondary",
                } }, params.row.roleName || "—")),
        },
        {
            field: "status",
            headerName: "Status",
            flex: 1,
            minWidth: 120,
            renderCell: (params) => {
                var _a;
                const isCurrentUser = params.row.email.toLowerCase() === ((_a = account === null || account === void 0 ? void 0 : account.username) === null || _a === void 0 ? void 0 : _a.toLowerCase());
                return (React.createElement(Box, { sx: { display: "flex", alignItems: "center", gap: 1 } },
                    React.createElement(Tooltip, { title: isCurrentUser ? "U kunt uw eigen status niet wijzigen" : "", arrow: true },
                        React.createElement("span", null,
                            React.createElement(Switch, { checked: params.row.status, onChange: (event, checked) => onStatusChange(params.row, checked), size: "small", color: "primary", disabled: !canUpdate("Users") || isCurrentUser })))));
            },
        },
        {
            field: "actions",
            headerName: "Acties",
            // flex: 1,
            minWidth: 120,
            sortable: false,
            filterable: false,
            renderCell: (params) => {
                var _a;
                const isCurrentUser = params.row.email.toLowerCase() === ((_a = account === null || account === void 0 ? void 0 : account.username) === null || _a === void 0 ? void 0 : _a.toLowerCase());
                return (React.createElement(Box, { sx: { display: "flex" } },
                    canUpdate("Users") && (React.createElement(Tooltip, { title: isCurrentUser
                            ? "U kunt uw eigen account niet bewerken"
                            : "Gebruiker bewerken", arrow: true },
                        React.createElement("span", null,
                            React.createElement(IconButton, { size: "small", onClick: (e) => {
                                    e.stopPropagation();
                                    onEdit(params.row);
                                }, disabled: isCurrentUser, sx: {
                                    border: "none",
                                    color: isCurrentUser ? "action.disabled" : "primary.main",
                                    "&:hover": {
                                        backgroundColor: isCurrentUser
                                            ? "transparent"
                                            : alpha(theme.palette.primary.main, 0.08),
                                    },
                                } },
                                React.createElement(Edit, { size: 20, variant: "Bulk" }))))),
                    canDelete("Users") && (React.createElement(Tooltip, { title: isCurrentUser
                            ? "U kunt uw eigen account niet verwijderen"
                            : "Gebruiker verwijderen", arrow: true },
                        React.createElement("span", null,
                            React.createElement(IconButton, { size: "small", onClick: (e) => {
                                    e.stopPropagation();
                                    onDelete(params.row);
                                }, disabled: isCurrentUser, sx: {
                                    color: isCurrentUser ? "action.disabled" : "error.main",
                                    border: "none",
                                    "&:hover": {
                                        backgroundColor: isCurrentUser
                                            ? "transparent"
                                            : alpha(theme.palette.error.main, 0.08),
                                    },
                                } },
                                React.createElement(TrashIcon, { size: 20 })))))));
            },
        },
    ];
    // Add these missing handlers
    const handleEditUser = (user) => {
        setEditUser(user);
        setNewUserDialogOpen(true);
    };
    const handleDeleteUser = (user) => {
        setUserToDelete(user);
        setDeleteConfirmOpen(true);
    };
    const handleStatusChange = (user, newStatus) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            yield updateUserStatus({
                userId: user.id,
                body: {
                    status: newStatus,
                },
            }).unwrap();
            // Refetch to update the UI
            refetch();
            enqueueSnackbar("Gebruikersstatus succesvol bijgewerkt", {
                variant: "success",
            });
        }
        catch (_a) {
            enqueueSnackbar("Fout bij het bijwerken van gebruikersstatus", {
                variant: "error",
            });
        }
    });
    // Debounce search term
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);
    // Filter users based on search term
    const filteredUsers = useMemo(() => {
        if (!debouncedSearchTerm)
            return users;
        const searchLower = debouncedSearchTerm.toLowerCase();
        return users.filter((user) => user.name.toLowerCase().includes(searchLower) ||
            user.email.toLowerCase().includes(searchLower) ||
            user.companies.some((company) => company.toLowerCase().includes(searchLower)));
    }, [users, debouncedSearchTerm]);
    // Local pagination state for the DataGrid
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    // Clamp page when the filteredUsers length or pageSize changes
    useEffect(() => {
        const maxPage = Math.max(0, Math.ceil(filteredUsers.length / pageSize) - 1);
        if (page > maxPage) {
            setPage(0);
        }
    }, [filteredUsers.length, pageSize]);
    const handleAddUser = (newUserData) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d, _e, _f;
        // Capture editUser before it gets cleared
        const userToEdit = editUser;
        if (userToEdit) {
            // Update existing user
            try {
                const userEmail = newUserData.email;
                const username = newUserData.name;
                // Get company IDs for all selected companies
                const companyIds = newUserData.allCompanies
                    ? []
                    : newUserData.companies
                        .map((companyName) => {
                        const company = allCompanies === null || allCompanies === void 0 ? void 0 : allCompanies.find((c) => c.entiteitNaam === companyName);
                        return company === null || company === void 0 ? void 0 : company.entiteitId;
                    })
                        .filter((id) => id !== undefined);
                if (!newUserData.allCompanies && companyIds.length === 0) {
                    enqueueSnackbar("Geen geldige bedrijven gevonden", {
                        variant: "error",
                    });
                    return;
                }
                const requestBody = {
                    userId: userToEdit.id,
                    body: {
                        companyIds,
                        userEmail,
                        username,
                        isActive: newUserData.status,
                        isDeleted: false,
                        roleId: newUserData.roleId,
                        allCompanies: newUserData.allCompanies,
                    },
                };
                yield updateUser(requestBody).unwrap();
                // Refetch the users list from API
                refetch();
                setEditUser(null);
                enqueueSnackbar("Gebruiker succesvol bijgewerkt", {
                    variant: "success",
                });
            }
            catch (_g) {
                enqueueSnackbar("Fout bij het bijwerken van gebruiker", {
                    variant: "error",
                });
            }
        }
        else {
            // Add new user - call API with all selected companies
            try {
                const userEmail = newUserData.email;
                const username = newUserData.name;
                // Get company IDs for all selected companies
                const companyIds = newUserData.allCompanies
                    ? []
                    : newUserData.companies
                        .map((companyName) => {
                        const company = allCompanies === null || allCompanies === void 0 ? void 0 : allCompanies.find((c) => c.entiteitNaam === companyName);
                        return company === null || company === void 0 ? void 0 : company.entiteitId;
                    })
                        .filter((id) => id !== undefined);
                // Create user with all companies in one call
                yield createUser({
                    companyIds,
                    userEmail,
                    username,
                    roleId: newUserData.roleId,
                    allCompanies: newUserData.allCompanies,
                }).unwrap();
                // Refetch the users list from API
                refetch();
                enqueueSnackbar("Gebruiker succesvol aangemaakt", {
                    variant: "success",
                });
            }
            catch (error) {
                // Check if error is due to duplicate user/email
                const isDuplicate = (error === null || error === void 0 ? void 0 : error.status) === 409 ||
                    ((_b = (_a = error === null || error === void 0 ? void 0 : error.data) === null || _a === void 0 ? void 0 : _a.message) === null || _b === void 0 ? void 0 : _b.toLowerCase().includes("already exists")) ||
                    ((_d = (_c = error === null || error === void 0 ? void 0 : error.data) === null || _c === void 0 ? void 0 : _c.message) === null || _d === void 0 ? void 0 : _d.toLowerCase().includes("duplicate")) ||
                    ((_f = (_e = error === null || error === void 0 ? void 0 : error.data) === null || _e === void 0 ? void 0 : _e.message) === null || _f === void 0 ? void 0 : _f.toLowerCase().includes("bestaat al"));
                enqueueSnackbar(isDuplicate
                    ? "Gebruiker bestaat al"
                    : "Fout bij het aanmaken van gebruiker", { variant: "error" });
            }
        }
    });
    const confirmDelete = () => __awaiter(void 0, void 0, void 0, function* () {
        if (userToDelete) {
            try {
                yield deleteUser({
                    userId: userToDelete.id,
                }).unwrap();
                // Refetch to update the UI
                refetch();
                setDeleteConfirmOpen(false);
                setUserToDelete(null);
                enqueueSnackbar("Gebruiker succesvol verwijderd", {
                    variant: "success",
                });
            }
            catch (_a) {
                setDeleteConfirmOpen(false);
                setUserToDelete(null);
                enqueueSnackbar("Fout bij het verwijderen van gebruiker", {
                    variant: "error",
                });
            }
        }
    });
    const columns = useMemo(() => createUserColumns(handleEditUser, handleDeleteUser, handleStatusChange), [canUpdate, canDelete]);
    return (React.createElement(Box, null,
        React.createElement(PageHeader, { startActions: React.createElement(FilteredInput, { placeholder: "Zoeken", value: searchTerm, onChange: (e) => setSearchTerm(e.target.value), endIcon: React.createElement(SearchNormal1, { size: 20, color: "currentColor" }) }), actions: React.createElement(React.Fragment, null,
                !isMobile && (React.createElement(React.Fragment, null, canCreate("Users") && (React.createElement(Button, { variant: "contained", color: "tertiary", size: "large", startIcon: React.createElement(Add, { size: 24 }), onClick: () => {
                        setEditUser(null);
                        setNewUserDialogOpen(true);
                    } }, "Nieuwe gebruiker")))),
                isMobile && canCreate("Users") && (React.createElement(Tooltip, { title: "Nieuwe gebruiker", arrow: true },
                    React.createElement(IconButton, { color: "primary", sx: {
                            backgroundColor: theme.palette.tertiary[40],
                            color: "white",
                            "&:hover": {
                                backgroundColor: "primary.dark",
                            },
                        }, onClick: () => {
                            setEditUser(null);
                            setNewUserDialogOpen(true);
                        } },
                        React.createElement(Add, { size: 24 }))))) }),
        permissionsLoading ? (React.createElement(Box, { sx: { height: 300 } },
            React.createElement(WidgetLoader, { isLoading: true }))) : !canRead("Users") ? (React.createElement(NoModuleAccess, { message: "U heeft geen toegang tot de gebruikersmodule" })) : isMobile ? (React.createElement(Box, { sx: {
                display: "flex",
                flexDirection: "column",
                gap: 2,
                width: "100%",
                maxWidth: "100%",
                overflowX: "hidden",
            } },
            filteredUsers
                .slice(page * pageSize, page * pageSize + pageSize)
                .map((user) => {
                var _a;
                return (React.createElement(SettingUserCard, { key: user.id, user: user, isCurrentUser: user.email.toLowerCase() === ((_a = account === null || account === void 0 ? void 0 : account.username) === null || _a === void 0 ? void 0 : _a.toLowerCase()), canUpdate: canUpdate("Users"), canDelete: canDelete("Users"), onStatusChange: handleStatusChange, onEdit: handleEditUser, onDelete: handleDeleteUser }));
            }),
            React.createElement(CardPagination, { count: filteredUsers.length, page: page, pageSize: pageSize, onPageChange: setPage, onPageSizeChange: (s) => {
                    setPageSize(s);
                    setPage(0);
                }, pageSizeOptions: [5, 10, 25] }))) : (React.createElement(PrimaryGrid, { loading: isLoading, rows: filteredUsers, columns: columns, getRowId: (row) => row.id, pagination: true, paginationMode: "client", paginationModel: { page, pageSize }, onPaginationModelChange: ({ page: newPage, pageSize: newPageSize, }) => {
                setPage(newPage);
                setPageSize(newPageSize);
            }, pageSizeOptions: [5, 10, 25], rowCount: filteredUsers.length, heightVariant: "full", sx: {
                "& .MuiDataGrid-cell": {
                    display: "flex",
                    alignItems: "center",
                },
            }, gridHeight: "calc(100dvh - 290px)" })),
        React.createElement(AddEditUserDialog, { open: newUserDialogOpen, onClose: () => {
                setNewUserDialogOpen(false);
                setEditUser(null);
            }, onSave: handleAddUser, editUser: editUser, availableCompanies: (allCompanies === null || allCompanies === void 0 ? void 0 : allCompanies.map((c) => c.entiteitNaam)) || [], isLoading: isCreating || isUpdating }),
        React.createElement(ConfirmDialog, { actionText: "Verwijderen", screenName: "Gebruiker", isOpen: deleteConfirmOpen, onClose: () => setDeleteConfirmOpen(false), onConfirm: confirmDelete, loading: isDeleting })));
};
